import { AttrType, Expression, SegmentRead, StoreItemRead, Token, TokenType } from '@/api/dashboard'
import { Attribute, CAMPAIGN_ATTRIBUTES, OperatorGeneralType, OperatorType, SelectorValueType } from '@/expr/types'
import { isMoneyAttribute } from '../common'
import { FLAGS, formatMoney } from '@/util'

import { useTranslation } from 'react-i18next'
import { useSegmentsQuery } from '@/layouts/segment/api/useSegmentsQuery'
import { useGenerateCompanyPath } from '@/libs'
import { useMemo } from 'react'
import { useGameItemsQuery } from '@/api/useGameItems'
import { useStoreItemByIdsQuery } from '@/layouts/components/InputStoreItem/useStoreItemByIdsQuery'
import { Badge } from '@/ui'
import { SKUItem } from '@/api/types'
import i18next from 'i18next'
import { findAttr } from '@/expr/findAttr'

export function getLeftPart(
  leftOperand: Token,
  t: (key: string) => string,
): { leftPart: JSX.Element | string; attrId: string } {
  let leftPart = leftOperand.value
  let attrId = ''

  switch (leftOperand.type) {
    case TokenType.CustomAttribute:
    case TokenType.Attribute: {
      const attr = findAttr(leftOperand.value)
      const name = attr?.name || leftOperand.value
      attrId = leftOperand.value
      leftPart = (
        <div className="inline-flex items-center">
          <span className="text-[14px] text-fg-brand-primary">{t('campaign.player')}</span>
          <span className="text-[14px] text-text-secondary">({name})</span>
        </div>
      )
      break
    }
    case TokenType.EventAttribute: {
      const attr = Object.values(CAMPAIGN_ATTRIBUTES)
        .flat()
        .find(a => a.id === leftOperand.value) as Attribute
      attrId = attr?.id || ''
      leftPart = (
        <div className="inline-flex items-center">
          <span className="text-[14px] text-fg-brand-primary">{t('campaign.event')}</span>
          <span className="text-[14px] text-text-secondary">({attr?.name})</span>
        </div>
      )
      break
    }
  }

  return { leftPart, attrId }
}

function getOperator(leftAttr: Attribute | undefined, operatorType: string, t: (key: string) => string): string {
  if (!leftAttr) {
    return t(`campaign.operator.${operatorType}`)
  }

  if (leftAttr.generalType === OperatorGeneralType.apply_or_not) {
    if (operatorType === OperatorType['==']) {
      return t('campaign.operator.apply')
    }
    if (operatorType === OperatorType['!=']) {
      return t('campaign.operator.not_apply')
    }
  }

  if (leftAttr.generalType === OperatorGeneralType.contains_or_not) {
    if (operatorType === OperatorType['==']) {
      return t('campaign.operator.contains')
    }
    if (operatorType === OperatorType['!=']) {
      return t('campaign.operator.not_contains')
    }
  }

  return leftAttr.type === AttrType.Date
    ? t(`campaign.operator.date.${operatorType}`)
    : t(`campaign.operator.${operatorType}`)
}

function getValue(
  value: string,
  typeValue: TokenType,
  operatorType: string,
  leftAttrId: string,
  leftAttr: Attribute | undefined,
  segments: SegmentRead[],
  items: SKUItem[],
  storeItems: StoreItemRead[],
  t: (key: string) => string,
): React.ReactNode {
  if (leftAttr?.generalType === OperatorGeneralType.apply_or_not) {
    return storeItems.find(s => s.id === value)?.item.name || value
  }

  if (leftAttr?.generalType === OperatorGeneralType.contains_or_not) {
    switch (leftAttr.selectorValueType) {
      case SelectorValueType.sku_no_price:
      case SelectorValueType.sku: {
        const item = items.find(it => it.id === value)
        if (!item) {
          return ''
        }
        return item.archived_at ? <Badge variant="red-secondary">{t('archived')}</Badge> : item.name
      }
      case SelectorValueType.segment:
        return segments.find(it => it.id === value)?.name || value
    }
  }

  if (typeValue === TokenType.Boolean) {
    return value ? 'True' : 'False'
  }

  if (leftAttr?.selectorValueType == SelectorValueType.order_source) {
    if (!Array.isArray(value)) {
      return t(`dashboard.source.${value}`)
    }
    return `(${(value as unknown as string[]).map(it => t(`dashboard.source.${it}`)).join(', ')})`
  }

  if (leftAttr?.selectorValueType == SelectorValueType.country) {
    return (
      <div className="inline-flex items-center gap-1 truncate">
        <img className="h-2.5" src={`${FLAGS}/${value}.svg`} loading="lazy" alt={value} />
        {i18next.t(value, { ns: 'countries' })}
      </div>
    )
  }

  if (typeValue === TokenType.List && Array.isArray(value)) {
    const values = isMoneyAttribute(leftAttrId)
      ? (value as unknown as number[]).map(it => formatMoney(it)).join(', ')
      : value.join(', ')
    return `(${values})`
  }

  switch (operatorType) {
    case OperatorType.in_list:
    case OperatorType.not_in_list:
      if (!Array.isArray(value)) {
        return value
      }
      return `(${(value as unknown as string[]).join(', ')})`
    case OperatorType.is_none:
    case OperatorType.is_not_none:
      return ''
    case OperatorType.in_range:
    case OperatorType.not_in_range: {
      if (!value || !Array.isArray(value)) {
        return ''
      }
      const [start, end] = value as unknown as [number, number]
      return isMoneyAttribute(leftAttrId) ? `${formatMoney(start)} - ${formatMoney(end)}` : `${start} - ${end}`
    }
    default:
      return isMoneyAttribute(leftAttrId) ? formatMoney(value as unknown as number) : value
  }
}

export const DescConditionNode = ({ expr }: { expr: Expression }) => {
  const { companyId, gameId } = useGenerateCompanyPath()
  const { t } = useTranslation()

  const tokensBy3items = useMemo(() => {
    const result: Token[][] = []
    if (!expr.tokens?.length) {
      return []
    }

    for (let i = 0; i < expr.tokens.length; i += 4) {
      result.push([expr.tokens[i - 1], expr.tokens[i], expr.tokens[i + 1], expr.tokens[i + 2]])
    }
    return result
  }, [expr.tokens])

  const itemIds = useMemo(() => {
    const ids: string[] = []

    if (!expr.tokens?.length) {
      return []
    }

    for (let i = 0; i < tokensBy3items.length; i++) {
      const group = tokensBy3items[i]
      const leftOperand = group[1]
      const leftAttr = findAttr(leftOperand.value)
      const value = group[3].value
      if (!leftAttr) {
        continue
      }
      if (
        (leftAttr && leftAttr.selectorValueType == SelectorValueType.sku_no_price) ||
        leftAttr.selectorValueType == SelectorValueType.sku
      ) {
        ids.push(value)
      }
    }
    return ids
  }, [tokensBy3items])

  const storeItemIds = useMemo(() => {
    const ids: string[] = []
    if (!expr.tokens?.length) {
      return []
    }

    for (let i = 0; i < tokensBy3items.length; i++) {
      const group = tokensBy3items[i]
      const leftOperand = group[1]
      const leftAttr = findAttr(leftOperand.value)
      const value = group[3].value
      if (leftAttr?.selectorValueType == SelectorValueType.store_item) {
        ids.push(value)
      }
    }
    return ids
  }, [tokensBy3items])

  const { data: segments = [] } = useSegmentsQuery(companyId, gameId, {
    limit: 100,
  })

  const { data: items = [] } = useGameItemsQuery(companyId, gameId, {
    ids: itemIds.join(','),
  })

  const { data: storeItems = [] } = useStoreItemByIdsQuery(companyId, gameId, storeItemIds)

  if (!expr?.tokens?.length) {
    return ''
  }

  const node = tokensBy3items.map((group, idx) => {
    const leftOperand = group[1]
    const operatorToken = group[2]
    const valueToken = group[3]
    const { leftPart, attrId: leftAttrId } = getLeftPart(leftOperand, t)
    const leftAttr = findAttr(leftOperand.value)
    const operator = getOperator(leftAttr, operatorToken.value, t)
    const value = getValue(
      valueToken.value,
      valueToken.type,
      operatorToken.value,
      leftAttrId,
      leftAttr,
      segments,
      items,
      storeItems,
      t,
    )
    const ifOrAnd = idx === 0 ? 'if' : group[0]?.value

    return (
      <div key={idx}>
        <span className="mr-1 text-[14px] text-text-secondary">{ifOrAnd}</span>
        {leftPart}
        <span className="ml-1 text-[14px] text-text-secondary">{operator}</span>
        {value != null && value != undefined && (
          <span className="ml-1 text-[14px]" style={{ color: '#AF6705' }}>
            {value}
          </span>
        )}
      </div>
    )
  })

  return <div className="max-h-[62px] overflow-hidden">{node.map(n => n)}</div>
}
